import { useContext, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";

import HeaderBar from "../../components/HeaderBar";
import PayRollLayoutWrapper from "../../components/PayRollLayoutWrapper";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";

import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import {
  Plus,
  Search,
  Trash2,
  RefreshCw,
  Filter,
  ArrowLeft,
  XCircle,
} from "lucide-react";

// ✅ change this to your server base
const BASE_URL = "http://nissi-india.com"; // <-- replace with your server

function safeStr(v) {
  return v == null ? "" : String(v);
}

function pickBranchId(row) {
  return (
    row?.fld_Branchid
  );
}
function pickBranchName(row) {
  return (
    row?.fld_BranchName
  );
}

function pickDeptId(row) {
  return row?.fld_deptid ?? row?.deptId ?? row?.DepartmentId ?? row?.value ?? "";
}
function pickDeptName(row) {
  return row?.fld_departmentname ?? row?.deptName ?? row?.text ?? row?.label ?? "";
}

function getMonthlyId(r) {
  return Number(
    r?.fld_MonthlyAttendance_ID ??
      r?.fld_monthlyattendance_id ??
      r?.MonthlyAttendanceId ??
      r?.monthlyAttendanceId ??
      0
  );
}
function getEmployeeId(r) {
  return Number(r?.fld_Employeeid ?? r?.fld_employeeid ?? r?.EmployeeId ?? r?.employeeId ?? 0);
}

function toDDMMYYYYFromISO(iso) {
  // iso: "2026-02-05"
  if (!iso) return "";
  const parts = String(iso).split("-");
  if (parts.length !== 3) return "";
  const [yyyy, mm, dd] = parts;
  return `${dd}/${mm}/${yyyy}`;
}

export default function MonthlyAttendanceListing() {
  const nav = useNavigate();
  const { theme } = useContext(ThemeContext);

  const isDark = theme === "dark";
  const skin = getCurrentSkin?.() || "theme1";

  // --- styles (same pattern you used earlier) ---
  const pageBg = isDark ? "bg-[#0b1220] text-white" : "bg-[#f6f7fb] text-gray-900";
  const cardBase = isDark
    ? "bg-white/5 border border-white/10 rounded-3xl shadow-xl"
    : "bg-white border border-gray-200 rounded-3xl shadow-xl";

  const labelBase = isDark ? "text-gray-200 font-bold text-sm" : "text-gray-700 font-bold text-sm";
  const inputBase =
    "px-4 py-3 rounded-2xl outline-none border text-sm font-semibold " +
    (isDark
      ? "bg-white/5 border-white/10 text-white placeholder:text-gray-400"
      : "bg-white border-gray-200 text-gray-900 placeholder:text-gray-400");

  const btnBase =
    "inline-flex items-center justify-center gap-2 rounded-2xl px-5 py-3 font-extrabold text-sm transition active:scale-[0.99] disabled:opacity-60 disabled:cursor-not-allowed";

  const primaryBtn =
    btnBase + " " + (isDark ? "bg-emerald-500 hover:bg-emerald-400 text-white" : "bg-emerald-600 hover:bg-emerald-700 text-white");
  const grayBtn =
    btnBase + " " + (isDark ? "bg-white/10 hover:bg-white/15 text-white" : "bg-gray-100 hover:bg-gray-200 text-gray-900");
  const dangerBtn =
    btnBase + " " + (isDark ? "bg-red-600 hover:bg-red-500 text-white" : "bg-red-600 hover:bg-red-700 text-white");

  const chip =
    "inline-flex items-center gap-2 px-4 py-2 rounded-full text-xs font-extrabold " +
    (isDark ? "bg-white/10 text-white" : "bg-gray-100 text-gray-800");

  // --- auth-ish headers (like your other pages) ---
  const finYearId = safeStr(localStorage.getItem("finYearId") || "1014");
  const loginUserId = safeStr(localStorage.getItem("userId") || localStorage.getItem("loginUserId") || "37");
  const sessionBranchId = safeStr(localStorage.getItem("branchId") || "");
  const branchType = safeStr(localStorage.getItem("branchType") || "HO");

  const axiosCfg = useMemo(() => {
    return {
      baseURL: BASE_URL,
      headers: {
        "x-loginuserid": loginUserId,
        "x-branchid": sessionBranchId,
        "x-branchtype": branchType,
      },
    };
  }, [loginUserId, sessionBranchId, branchType]);

  // --- meta state ---
  const [metaLoading, setMetaLoading] = useState(false);
  const [branches, setBranches] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [months, setMonths] = useState([]); // [{name,val}]
  const [years, setYears] = useState(["Select"]);

  // --- filters ---
  const [branchId, setBranchId] = useState("Select");
  const [fromDateISO, setFromDateISO] = useState(""); // input type=date ISO
  const [toDateISO, setToDateISO] = useState("");
  const [monthVal, setMonthVal] = useState("Select"); // "1".."12"
  const [year, setYear] = useState("Select");
  const [departmentId, setDepartmentId] = useState("Select");

  // --- table state ---
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [deleteAllowed, setDeleteAllowed] = useState(true);

  const [selectedMap, setSelectedMap] = useState({}); // {monthlyId: true}
  const selectedIds = useMemo(() => Object.keys(selectedMap).filter((k) => selectedMap[k]), [selectedMap]);

  // --- pagination ---
  const [page, setPage] = useState(1);
  const pageSize = 15;

  const totalPages = useMemo(() => {
    return Math.max(1, Math.ceil((rows?.length || 0) / pageSize));
  }, [rows]);

  const pagedRows = useMemo(() => {
    const start = (page - 1) * pageSize;
    return (rows || []).slice(start, start + pageSize);
  }, [rows, page]);

  // --- load META ---
  const loadMeta = async () => {
    try {
      setMetaLoading(true);
      console.log("[MAL][UI] meta -> finYearId:", finYearId);

      const res = await axios.get(`/api/payroll/monthly-attendance-listing/meta`, {
        ...axiosCfg,
        params: { finYearId },
      });

      if (!res.data?.success) throw new Error(res.data?.message || "Meta failed");

      const m = res.data;
      setBranches(m.branches || []);
      setDepartments(m.departments || []);
      setMonths(m.months || []);
      setYears(m.years || ["Select"]);

      // defaults
      setBranchId(m.defaultBranchId || "Select");
      setMonthVal(m.defaultMonth || "Select");
      setYear(m.defaultYear || "Select");
      setDepartmentId("Select");

      // clear dates
      setFromDateISO("");
      setToDateISO("");

      console.log("[MAL][UI] meta ok:", {
        branches: (m.branches || []).length,
        departments: (m.departments || []).length,
        months: (m.months || []).length,
        years: (m.years || []).length,
      });
    } catch (e) {
      console.error("[MAL][UI] meta error:", e);
      toast.error(e?.message || "Failed to load meta");
    } finally {
      setMetaLoading(false);
    }
  };

  useEffect(() => {
    loadMeta();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // --- search ---
  const onSearch = async () => {
    try {
      if (!branchId || branchId === "Select") return toast.error("Select Branch");
      if (!monthVal || monthVal === "Select") return toast.error("Select Month");
      if (!year || year === "Select") return toast.error("Select Year");

      setLoading(true);
      setSelectedMap({});
      setPage(1);

      const payload = {
        branchId: safeStr(branchId),
        monthVal: safeStr(monthVal),
        year: safeStr(year),
        departmentId: safeStr(departmentId || "Select"),
        // for future use (ASP doesn't use these in query)
        fromDate: toDDMMYYYYFromISO(fromDateISO),
        toDate: toDDMMYYYYFromISO(toDateISO),
      };

      console.log("[MAL][UI] search payload:", payload);

      const res = await axios.post(`/api/payroll/monthly-attendance-listing/search`, payload, {
        ...axiosCfg,
        params: { finYearId },
      });

      if (!res.data?.success) {
        toast.error(res.data?.message || "No records");
        setRows([]);
        setDeleteAllowed(true);
        return;
      }

      setRows(res.data?.rows || []);
      setDeleteAllowed(res.data?.deleteAllowed !== false); // default true
      toast.success(`Loaded ${(res.data?.rows || []).length} record(s)`);
    } catch (e) {
      console.error("[MAL][UI] search error:", e);
      toast.error(e?.response?.data?.message || e?.message || "Search failed");
    } finally {
      setLoading(false);
    }
  };

  // --- select handlers ---
  const toggleRow = (monthlyId, checked) => {
    setSelectedMap((prev) => ({ ...prev, [monthlyId]: checked }));
  };

  const toggleAllOnPage = (checked) => {
    const ids = pagedRows.map((r) => getMonthlyId(r)).filter(Boolean);
    setSelectedMap((prev) => {
      const next = { ...prev };
      ids.forEach((id) => (next[id] = checked));
      return next;
    });
  };

  const allCheckedOnPage = useMemo(() => {
    const ids = pagedRows.map((r) => getMonthlyId(r)).filter(Boolean);
    if (ids.length === 0) return false;
    return ids.every((id) => selectedMap[id]);
  }, [pagedRows, selectedMap]);

  // --- delete selected ---
  const onDeleteSelected = async () => {
    try {
      if (!deleteAllowed) {
        return toast.error("Delete is allowed only for current/previous month (ASP rule).");
      }
      if (selectedIds.length === 0) return toast.error("Please select any one");

      const chosen = rows
        .filter((r) => selectedMap[getMonthlyId(r)])
        .map((r) => ({
          monthlyAttendanceId: getMonthlyId(r),
          employeeId: getEmployeeId(r),
        }))
        .filter((x) => x.monthlyAttendanceId && x.employeeId);

      if (chosen.length === 0) return toast.error("Selected rows missing IDs");

      setLoading(true);

      const payload = {
        monthVal: safeStr(monthVal),
        year: safeStr(year),
        rows: chosen,
      };

      console.log("[MAL][UI] delete payload:", payload);

      const res = await axios.post(`/api/payroll/monthly-attendance-listing/delete`, payload, {
        ...axiosCfg,
        params: { finYearId },
      });

      if (!res.data?.success) {
        toast.error(res.data?.message || "Delete failed");
        return;
      }

      toast.success(res.data?.message || "Deleted Successfully");

      // refresh list (ASP FillGrid again)
      await onSearch();
    } catch (e) {
      console.error("[MAL][UI] delete error:", e);
      toast.error(e?.response?.data?.message || e?.message || "Delete failed");
    } finally {
      setLoading(false);
    }
  };

  // --- add new (lockdown check) ---
  const onAddNew = async () => {
    try {
      setLoading(true);

      const res = await axios.get(`/api/payroll/monthly-attendance-listing/addnew-target`, {
        ...axiosCfg,
        params: { finYearId },
      });

      if (!res.data?.success) throw new Error(res.data?.message || "Addnew check failed");

      // match your routing:
      // target: "lockdown" or "entry"
      if (res.data.target === "lockdown") {
        nav("/payroll/MonthlyAttendanceLockDownEntry/lockdown");
      } else {
        nav("/payroll/MonthlyAttendanceLockDownEntry/entry");
      }
    } catch (e) {
      console.error("[MAL][UI] addnew error:", e);
      toast.error(e?.message || "Failed to open Monthly Attendance");
    } finally {
      setLoading(false);
    }
  };

  // --- row click view (ASP has View command, but your ASP code uses hidden ID) ---
  // If later you want View/Edit, we can store MAID like daily did and nav to entry.
  const onView = (r) => {
    const maid = getMonthlyId(r);
    if (!maid) return toast.error("MonthlyAttendance ID missing");
    localStorage.setItem("MAID", String(maid));
    nav("/payroll/MonthlyAttendanceLockDownEntry/entry"); // your entry page reads MAID for edit
  };

  return (
    <PayRollLayoutWrapper>
      <div className={`${pageBg} min-h-screen`}>
        <HeaderBar />

        <ToastContainer position="top-right" autoClose={2500} />

        <div className="max-w-7xl mx-auto px-4 py-6">
          {/* Header */}
          <div className={`${cardBase} p-6 mb-6`}>
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className={`p-3 rounded-2xl ${isDark ? "bg-white/10" : "bg-gray-100"}`}>
                  <Filter className={isDark ? "text-white" : "text-gray-800"} />
                </div>
                <div>
                  <div className="text-2xl font-extrabold leading-tight">Monthly Attendance Listing</div>
                  <div className={`text-sm font-semibold ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                    INFRA SCREEN : C-23
                  </div>
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-3 justify-start lg:justify-end">
                <button
                  onClick={() => nav(-1)}
                  className={grayBtn}
                  type="button"
                >
                  <ArrowLeft className="w-4 h-4" />
                  Back
                </button>

                <button onClick={onAddNew} className={primaryBtn} disabled={loading} type="button">
                  <Plus className="w-4 h-4" />
                  Add New
                </button>

                <button onClick={loadMeta} className={grayBtn} disabled={metaLoading} type="button">
                  <RefreshCw className="w-4 h-4" />
                  Reload
                </button>

                <div className={chip}>
                  Selected: <span className="text-base">{selectedIds.length}</span>
                </div>

                {!deleteAllowed && rows.length > 0 && (
                  <div className={`px-4 py-2 rounded-2xl text-xs font-extrabold flex items-center gap-2 ${isDark ? "bg-yellow-500/20 text-yellow-200" : "bg-yellow-100 text-yellow-800"}`}>
                    <XCircle className="w-4 h-4" />
                    Delete allowed only for current / previous month
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Filters */}
          <div className={`${cardBase} p-6 mb-6`}>
            <div className="flex items-center gap-2 mb-4">
              <Filter className={isDark ? "text-gray-200" : "text-gray-700"} />
              <div className="text-lg font-extrabold">Filters</div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 items-end">
              {/* Branch */}
              <div className="lg:col-span-4">
                <label className={labelBase}>
                  Branch <span className="text-red-500">*</span>
                </label>
                <select className={`${inputBase} w-full`} value={branchId} onChange={(e) => setBranchId(e.target.value)}>
                  <option value="Select">Select</option>
                  {branches.map((b, idx) => {
                    const id = safeStr(pickBranchId(b));
                    const name = safeStr(pickBranchName(b));
                    if (!id) return null;
                    return (
                      <option key={`${id}-${idx}`} value={id}>
                        {name || id}
                      </option>
                    );
                  })}
                </select>
              </div>

              {/* From Date */}
              <div className="lg:col-span-2">
                <label className={labelBase}>From Date</label>
                <input
                  type="date"
                  className={`${inputBase} w-full`}
                  value={fromDateISO}
                  onChange={(e) => setFromDateISO(e.target.value)}
                />
              </div>

              {/* To Date */}
              <div className="lg:col-span-2">
                <label className={labelBase}>To Date</label>
                <input
                  type="date"
                  className={`${inputBase} w-full`}
                  value={toDateISO}
                  onChange={(e) => setToDateISO(e.target.value)}
                />
              </div>

              {/* Year */}
              <div className="lg:col-span-2">
                <label className={labelBase}>
                  Year <span className="text-red-500">*</span>
                </label>
                <select className={`${inputBase} w-full`} value={year} onChange={(e) => setYear(e.target.value)}>
                  {years.map((y) => (
                    <option key={y} value={y}>
                      {y}
                    </option>
                  ))}
                </select>
              </div>

              {/* Month */}
              <div className="lg:col-span-2">
                <label className={labelBase}>
                  Month <span className="text-red-500">*</span>
                </label>
                <select className={`${inputBase} w-full`} value={monthVal} onChange={(e) => setMonthVal(e.target.value)}>
                  <option value="Select">Select</option>
                  {months.map((m) => (
                    <option key={m.val} value={m.val}>
                      {m.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Department */}
              <div className="lg:col-span-4">
                <label className={labelBase}>Department</label>
                <select
                  className={`${inputBase} w-full`}
                  value={departmentId}
                  onChange={(e) => setDepartmentId(e.target.value)}
                >
                  <option value="Select">Select</option>
                  {departments.map((d, idx) => {
                    const id = safeStr(pickDeptId(d));
                    const name = safeStr(pickDeptName(d));
                    if (!id) return null;
                    return (
                      <option key={`${id}-${idx}`} value={id}>
                        {name || id}
                      </option>
                    );
                  })}
                </select>
              </div>

              {/* Search Button */}
              <div className="lg:col-span-8 lg:justify-self-end">
                <div className="flex flex-wrap lg:flex-nowrap gap-3 justify-start lg:justify-end">
                  <button onClick={onSearch} className={primaryBtn} disabled={loading}>
                    <Search className="w-4 h-4" />
                    Search
                  </button>

                  <button onClick={onDeleteSelected} className={dangerBtn} disabled={loading || selectedIds.length === 0 || !deleteAllowed}>
                    <Trash2 className="w-4 h-4" />
                    Delete Selected
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Table */}
          <div className={`${cardBase} p-0 overflow-hidden`}>
            <div className={`px-6 py-4 flex items-center justify-between ${isDark ? "border-b border-white/10" : "border-b border-gray-200"}`}>
              <div className="font-extrabold">Results</div>
              <div className={`${isDark ? "text-gray-300" : "text-gray-600"} text-sm font-semibold`}>
                {rows.length} record(s)
              </div>
            </div>

            <div className="overflow-auto">
              <table className="min-w-[1200px] w-full">
                <thead className={`${isDark ? "bg-emerald-500/20" : "bg-emerald-600"} ${isDark ? "" : "text-white"}`}>
                  <tr>
                    <th className="px-4 py-4 text-left whitespace-nowrap">S.No</th>
                    <th className="px-4 py-4 text-left whitespace-nowrap">Employee Code</th>
                    <th className="px-4 py-4 text-left whitespace-nowrap">Employee Name</th>
                    <th className="px-4 py-4 text-left whitespace-nowrap">Designation</th>
                    <th className="px-4 py-4 text-left whitespace-nowrap">Opening Leave Balance</th>
                    <th className="px-4 py-4 text-left whitespace-nowrap">Days Worked</th>
                    <th className="px-4 py-4 text-left whitespace-nowrap">Casual Leave</th>
                    <th className="px-4 py-4 text-left whitespace-nowrap">Holiday</th>
                    <th className="px-4 py-4 text-left whitespace-nowrap">Manage Leave</th>
                    <th className="px-4 py-4 text-left whitespace-nowrap">LOP</th>
                    <th className="px-4 py-4 text-left whitespace-nowrap">OT</th>
                    <th className="px-4 py-4 text-left whitespace-nowrap">Remarks</th>
                    <th className="px-4 py-4 text-center whitespace-nowrap">
                      <div className="flex items-center justify-center gap-2">
                        <input
                          type="checkbox"
                          checked={allCheckedOnPage}
                          onChange={(e) => toggleAllOnPage(e.target.checked)}
                        />
                        <span className="font-extrabold">Select All</span>
                      </div>
                    </th>
                  </tr>
                </thead>

                <tbody>
                  {pagedRows.length === 0 ? (
                    <tr>
                      <td colSpan={13} className="px-6 py-10 text-center">
                        <div className={`${isDark ? "text-gray-300" : "text-gray-600"} font-extrabold`}>
                          No Records Found
                        </div>
                      </td>
                    </tr>
                  ) : (
                    pagedRows.map((r, idx) => {
                      const monthlyId = getMonthlyId(r);
                      const empId = getEmployeeId(r);
                      const lop = Number(r?.fld_lopdays ?? r?.fld_lopDays ?? 0);

                      const rowDanger = lop && lop !== 0;

                      return (
                        <tr
                          key={monthlyId || idx}
                          className={
                            rowDanger
                              ? "bg-red-600 text-white"
                              : isDark
                              ? "border-b border-white/10 hover:bg-white/5"
                              : "border-b border-gray-200 hover:bg-gray-50"
                          }
                        >
                          <td className="px-4 py-4 whitespace-nowrap font-bold">
                            {(page - 1) * pageSize + idx + 1}
                          </td>

                          <td className="px-4 py-4 whitespace-nowrap font-extrabold underline cursor-pointer" onClick={() => onView(r)}>
                            {r?.fld_EmployeeCode ?? r?.fld_employeecode ?? "-"}
                          </td>

                          <td className="px-4 py-4 whitespace-nowrap font-bold">
                            {r?.fld_Employee ?? r?.fld_employee ?? "-"}
                          </td>

                          <td className="px-4 py-4 whitespace-nowrap font-bold">
                            {r?.fld_designationname ?? r?.fld_DesignationName ?? "-"}
                          </td>

                          <td className="px-4 py-4 whitespace-nowrap font-bold">
                            {r?.fld_totleaveob ?? r?.fld_totleaveOB ?? "0"}
                          </td>

                          <td className="px-4 py-4 whitespace-nowrap font-bold">
                            {r?.fld_Daysworked ?? r?.fld_daysworked ?? "0"}
                          </td>

                          <td className="px-4 py-4 whitespace-nowrap font-bold">
                            {r?.fld_Leave ?? r?.fld_leave ?? "0"}
                          </td>

                          <td className="px-4 py-4 whitespace-nowrap font-bold">
                            {r?.fld_holiday ?? "0"}
                          </td>

                          <td className="px-4 py-4 whitespace-nowrap font-bold">
                            {r?.fld_ManageLeave ?? r?.fld_manageleave ?? "0"}
                          </td>

                          <td className="px-4 py-4 whitespace-nowrap font-extrabold">
                            {r?.fld_lopdays ?? r?.fld_lopDays ?? "0"}
                          </td>

                          <td className="px-4 py-4 whitespace-nowrap font-bold">
                            {r?.fld_OT ?? r?.fld_ot ?? "0"}
                          </td>

                          <td className="px-4 py-4 min-w-[250px] font-semibold">
                            {r?.fld_Remarks ?? r?.fld_remarks ?? ""}
                          </td>

                          <td className="px-4 py-4 text-center">
                            <input
                              type="checkbox"
                              checked={!!selectedMap[monthlyId]}
                              onChange={(e) => toggleRow(monthlyId, e.target.checked)}
                              disabled={!monthlyId || !empId}
                            />
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className={`px-6 py-4 flex flex-col sm:flex-row items-center justify-between gap-3 ${isDark ? "border-t border-white/10" : "border-t border-gray-200"}`}>
              <div className={`${isDark ? "text-gray-300" : "text-gray-600"} text-sm font-bold`}>
                Page {page} / {totalPages}
              </div>

              <div className="flex items-center gap-2">
                <button
                  className={grayBtn}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page <= 1}
                  type="button"
                >
                  Prev
                </button>
                <button
                  className={grayBtn}
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page >= totalPages}
                  type="button"
                >
                  Next
                </button>
              </div>
            </div>
          </div>

          <div className={`mt-4 text-sm ${isDark ? "text-gray-300" : "text-gray-600"} font-semibold`}>
            Note (ASP rule): Row becomes red when LOP is not 0. Delete is shown only for current/previous month.
          </div>
        </div>
      </div>
    </PayRollLayoutWrapper>
  );
}
